import i18n from "i18next";

/**
 * Digit systems the clinical app can render. Arabic-Indic digits (`arab`)
 * are an owner decision (DEC-18); until it is approved for a deployment the
 * app renders Western digits (`latn`).
 */
export const NUMBERING_SYSTEMS = ["latn", "arab"] as const;
export type NumberingSystem = (typeof NUMBERING_SYSTEMS)[number];
export const DEFAULT_NUMBERING_SYSTEM: NumberingSystem = "latn";

/**
 * Only the Gregorian calendar is displayed. Hijri display is an open owner
 * decision (DEC-18) and is never inferred from the language or region.
 */
const CALENDAR = "gregory";

const FALLBACK_LANGUAGE = "en";

export function parseNumberingSystem(value: string | undefined) {
  if (!value) {
    return DEFAULT_NUMBERING_SYSTEM;
  }
  if ((NUMBERING_SYSTEMS as readonly string[]).includes(value)) {
    return value as NumberingSystem;
  }
  console.warn(
    `Invalid REACT_NUMBERING_SYSTEM: "${value}". Valid values are: ${NUMBERING_SYSTEMS.join(", ")}. Falling back to ${DEFAULT_NUMBERING_SYSTEM}.`,
  );
  return DEFAULT_NUMBERING_SYSTEM;
}

let configuredNumberingSystem: NumberingSystem = DEFAULT_NUMBERING_SYSTEM;

export function configureNumberingSystem(numberingSystem: NumberingSystem) {
  configuredNumberingSystem = numberingSystem;
}

export function activeLanguage() {
  return i18n.resolvedLanguage || i18n.language || FALLBACK_LANGUAGE;
}

/**
 * The base language for dayjs, which only carries the locales registered in
 * `@/Utils/dayjs`; any other language keeps the English calendar strings.
 */
export function baseLanguage(language: string | undefined) {
  try {
    return new Intl.Locale(language || FALLBACK_LANGUAGE).language;
  } catch {
    return FALLBACK_LANGUAGE;
  }
}

export function formattingLocale(
  language: string = activeLanguage(),
  numberingSystem: NumberingSystem = configuredNumberingSystem,
) {
  let baseName = FALLBACK_LANGUAGE;
  try {
    baseName = new Intl.Locale(language || FALLBACK_LANGUAGE).baseName;
  } catch {
    baseName = FALLBACK_LANGUAGE;
  }
  return new Intl.Locale(baseName, {
    calendar: CALENDAR,
    numberingSystem,
  }).toString();
}

const formatters = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function cached<T extends Intl.DateTimeFormat | Intl.NumberFormat>(
  kind: string,
  locale: string,
  options: object,
  create: () => T,
) {
  const key = `${kind}|${locale}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key) as T | undefined;
  if (!formatter) {
    formatter = create();
    formatters.set(key, formatter);
  }
  return formatter;
}

export function dateTimeFormatter(options: Intl.DateTimeFormatOptions = {}) {
  const locale = formattingLocale();
  const resolved: Intl.DateTimeFormatOptions = {
    ...options,
    calendar: CALENDAR,
    numberingSystem: configuredNumberingSystem,
  };
  return cached(
    "date",
    locale,
    resolved,
    () => new Intl.DateTimeFormat(locale, resolved),
  );
}

export function numberFormatter(options: Intl.NumberFormatOptions = {}) {
  const locale = formattingLocale();
  const resolved: Intl.NumberFormatOptions = {
    ...options,
    numberingSystem: configuredNumberingSystem,
  };
  return cached(
    "number",
    locale,
    resolved,
    () => new Intl.NumberFormat(locale, resolved),
  );
}

export function formatDateTime(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = {},
) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return dateTimeFormatter(options).format(date);
}

export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions = {},
) {
  return numberFormatter(options).format(value);
}

/**
 * Formats an amount in the given currency. The currency code and the
 * fraction digits come from the caller unchanged; only the language,
 * calendar and digit system follow the active locale.
 */
export function formatMoney(
  value: number,
  options: Intl.NumberFormatOptions & { currency: string },
) {
  return numberFormatter({ ...options, style: "currency" }).format(value);
}
